import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircle,
  Globe,
  KeyRound,
  Loader2,
  Monitor,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { closeMiniApp } from "@telegram-apps/sdk-react";
import { dataTrigger, track } from "@/analytics";
import type { AppLoginInfo } from "@/api/app";
import { appErrorMessage, checkAppLogin, confirmAppLogin } from "@/api/app";
import { useAuth } from "@/store/authStore";
import { Button } from "@/ui/Button/Button";
import { onlyNumbers } from "@/utils/helpers";
import cls from "./styles.module.scss";

type Step =
  | { kind: "enter"; error?: string }
  | { kind: "checking" }
  | { kind: "ready"; info: AppLoginInfo }
  | { kind: "confirming"; info: AppLoginInfo }
  | { kind: "done"; web: boolean }
  | { kind: "rejected" }
  | { kind: "error"; message: string };

const DISABLED = "Вход в приложение БКоннект пока недоступен";

const PLATFORMS: Record<string, string> = {
  web: "Сайт БКоннект",
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
};

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Head({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className={cls.head}>
      <div className={cls.iconBubble}>{icon}</div>
      <h1 className={cls.title}>{title}</h1>
      <p className={cls.text}>{text}</p>
    </div>
  );
}

type Props = {
  /** Код из ссылки startapp=applogin_<код>; пустой — человек введёт его сам. */
  initialCode: string;
  onClose: () => void;
};

/**
 * Подтверждение входа в приложение BK VPN на компьютере. Приложение показывает
 * ссылку или QR на мини-апп с кодом; здесь человек видит, какое устройство просит
 * вход, и подтверждает. Личность — по подписи Telegram (initData), не по JWT.
 */
export function AppLoginScreen({ initialCode, onClose }: Props) {
  const initData = useAuth((s) => s.initData);
  const [code, setCode] = useState(initialCode);
  const [step, setStep] = useState<Step>(
    initialCode ? { kind: "checking" } : { kind: "enter" }
  );
  const started = useRef(false);

  const check = useCallback(
    async (value: string) => {
      if (!initData) return;
      setStep({ kind: "checking" });
      try {
        const info = await checkAppLogin(value, initData);
        setStep({ kind: "ready", info });
      } catch (error) {
        setStep({
          kind: "error",
          message: appErrorMessage(
            error,
            "Не удалось проверить код. Попробуйте ещё раз",
            DISABLED
          ),
        });
      }
    },
    [initData]
  );

  useEffect(() => {
    track("bkn.app_login.show");
  }, []);

  useEffect(() => {
    if (started.current || !initialCode || !initData) return;
    started.current = true;
    check(initialCode);
  }, [check, initData, initialCode]);

  const confirm = async (info: AppLoginInfo) => {
    if (!initData) return;
    setStep({ kind: "confirming", info });
    try {
      await confirmAppLogin(code, initData);
      setStep({ kind: "done", web: info.platform === "web" });
    } catch (error) {
      setStep({
        kind: "error",
        message: appErrorMessage(
          error,
          "Не удалось подтвердить вход. Попробуйте ещё раз",
          DISABLED
        ),
      });
    }
  };

  const submitCode = (e: React.FormEvent) => {
    e.preventDefault();
    const value = onlyNumbers(code);
    if (value.length !== 6) {
      setStep({ kind: "enter", error: "Код — 6 цифр из приложения БКоннект" });
      return;
    }
    setCode(value);
    check(value);
  };

  const closeApp = () => {
    if (closeMiniApp.isAvailable()) closeMiniApp();
    else onClose();
  };

  let body: React.ReactNode;

  if (!initData) {
    body = (
      <>
        <Head
          icon={<ShieldAlert />}
          title="Откройте в Telegram"
          text="Подтвердить вход можно только в БКоннекте внутри Telegram. Нажмите «Открыть Telegram» в приложении БКоннект ещё раз или отсканируйте QR-код телефоном."
        />
        <Button
          variant="secondary"
          size="lg"
          onClick={onClose}
          {...dataTrigger("bkn.app_login.home")}
        >
          На главную
        </Button>
      </>
    );
  } else if (step.kind === "enter") {
    body = (
      <>
        <Head
          icon={<KeyRound />}
          title="Вход в приложение"
          text="Введите код, который показывает приложение БКоннект на компьютере"
        />
        <form className={cls.form} onSubmit={submitCode}>
          <input
            className={cls.codeInput}
            value={code}
            onChange={(e) => setCode(onlyNumbers(e.target.value).slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            aria-label="Код из приложения БКоннект"
          />
          {step.error ? <p className={cls.error}>{step.error}</p> : null}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            {...dataTrigger("bkn.app_login.enter_code")}
          >
            Продолжить
          </Button>
        </form>
        <Button
          variant="secondary"
          size="md"
          onClick={onClose}
          {...dataTrigger("bkn.app_login.home")}
        >
          На главную
        </Button>
      </>
    );
  } else if (step.kind === "checking") {
    body = (
      <div className={cls.loading}>
        <Loader2 className={cls.spinner} />
        Проверяем код…
      </div>
    );
  } else if (step.kind === "ready" || step.kind === "confirming") {
    const { info } = step;
    const busy = step.kind === "confirming";
    // вход на сайте (браузер) — то же подтверждение, другие слова
    const web = info.platform === "web";
    body = (
      <>
        <Head
          icon={web ? <Globe /> : <Monitor />}
          title={web ? "Вход на сайте БКоннект" : "Вход в приложение"}
          text={
            web
              ? "Браузер просит войти в ваш аккаунт БКоннект"
              : "Приложение на компьютере просит войти в ваш аккаунт БКоннект"
          }
        />
        <dl className={cls.details}>
          <div className={cls.row}>
            <dt>{web ? "Браузер" : "Устройство"}</dt>
            <dd>{info.device_name || "без имени"}</dd>
          </div>
          <div className={cls.row}>
            <dt>Система</dt>
            <dd>{PLATFORMS[info.platform] ?? (info.platform || "—")}</dd>
          </div>
          <div className={cls.row}>
            <dt>Запрос</dt>
            <dd>в {formatTime(info.created_at)}</dd>
          </div>
          <div className={cls.row}>
            <dt>Код</dt>
            <dd className={cls.code}>{code}</dd>
          </div>
        </dl>
        <p className={cls.warning}>
          <ShieldAlert className={cls.warningIcon} />
          {web
            ? "Подтверждайте, только если сами нажали «Войти через Telegram» на сайте БКоннект и видите там этот же код."
            : "Подтверждайте, только если сами открыли вход в приложении БКоннект на своём компьютере и видите там этот же код."}
        </p>
        <div className={cls.actions}>
          <Button
            variant="primary"
            size="lg"
            onClick={() => confirm(info)}
            disabled={busy}
            leftIcon={busy ? <Loader2 className={cls.spinner} /> : undefined}
            {...dataTrigger("bkn.app_login.confirm")}
          >
            {busy ? "Подтверждаем…" : "Подтвердить вход"}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={() => setStep({ kind: "rejected" })}
            disabled={busy}
            {...dataTrigger("bkn.app_login.reject")}
          >
            Это не я
          </Button>
        </div>
      </>
    );
  } else if (step.kind === "done") {
    body = (
      <>
        <Head
          icon={<CheckCircle />}
          title="Вход подтверждён"
          text={
            step.web
              ? "Вернитесь в браузер — сайт БКоннект войдёт сам через несколько секунд."
              : "Вернитесь в приложение БКоннект на компьютере — оно войдёт само через несколько секунд."
          }
        />
        <Button
          variant="primary"
          size="lg"
          onClick={closeApp}
          {...dataTrigger("bkn.app_login.close")}
        >
          Готово
        </Button>
      </>
    );
  } else if (step.kind === "rejected") {
    body = (
      <>
        <Head
          icon={<XCircle />}
          title="Вход не подтверждён"
          text="На компьютер ничего не передано, код сгорит сам через несколько минут."
        />
        <Button
          variant="secondary"
          size="lg"
          onClick={onClose}
          {...dataTrigger("bkn.app_login.home")}
        >
          На главную
        </Button>
      </>
    );
  } else {
    // вход выключен в админке — вводить код бесполезно
    const canRetry = step.message !== DISABLED;
    body = (
      <>
        <Head icon={<XCircle />} title="Не получилось" text={step.message} />
        <div className={cls.actions}>
          {canRetry ? (
            <Button
              variant="primary"
              size="lg"
              onClick={() => setStep({ kind: "enter" })}
              {...dataTrigger("bkn.app_login.retry")}
            >
              Ввести код вручную
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
            {...dataTrigger("bkn.app_login.home")}
          >
            На главную
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className={cls.screen}>
      <div className={cls.card}>{body}</div>
    </div>
  );
}
